import {
    TargetableObjectNode,
    BooleanInfo,
    CompileTemplate,
    ConfigGroup,
    ConfigInfo,
    ConfigVariant,
    deleteNodesFromTemplateGraph,
    getInterfaceIdPrefix,
    getNodeOwner,
    getReferencingDeletedTemplateNodes,
    getTemplateNodeLabel,
    getUniqueTemplateNodeParent,
    GraphBuilder,
    GraphScheduler,
    ImageInfo,
    InterfaceDescriptor,
    ISceneManager,
    isTemplateContainer,
    JsonInfo,
    MaterialAssignment,
    MaterialAssignments,
    MaterialInfo,
    MaterialReference,
    NodeEvaluator,
    TemplateNodes,
    NotReady,
    NumberInfo,
    ObjectNode,
    ObjectInfo,
    Parameters,
    PdfSettings,
    ReifiedMeshData,
    RunTemplate,
    SceneNodes,
    Solver,
    SolverState,
    StoredMesh,
    StringInfo,
    TemplateContext,
    TemplateGraph,
    TemplateGraphDifferences,
    TemplateGraphSnapshot,
    TemplateInfo,
    TemplateInstance,
    TemplateParameterValue,
    TransformAccessor,
    LiveValueAccessor,
    OutputNode,
    isNodeWithMatrixArrayLockedTransformParameter,
    GeomBuilderContext,
    CurveInfo,
    MeshInfo,
    NodesInfo,
    InfoNodeId,
    InfoNodes,
    IMeshGeometryQueries,
    SceneManagerTask,
    MeshCurve,
    TemplateNode,
    AnyJsonValue,
    getControlPointPartNumber,
    InfoNodePart,
    Intersection,
    isControlPointPart,
    LodTypeValue,
    SceneNodePart,
    TemplateNodePart,
    TransformMode,
    ContextOtherData,
    ObjectPathPart,
    HashedMaterialNodeWithColorOutput,
    isProceduralTemplateNode,
    objectPathStartsWith,
    getRootTemplateNodeForObjectPath,
} from "@cm/template-nodes"
import {computed, DestroyRef, effect, inject, Injectable, Injector, OnDestroy, Signal, signal, untracked} from "@angular/core"
import type {CmLogger} from "@cm/log"
import {CM_LOGGER_SCENE} from "@common/services/logging"
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop"
import {MatDialog, MatDialogRef} from "@angular/material/dialog"
import {DialogComponent} from "@app/common/components/dialogs/dialog/dialog.component"
import {encodeConfiguratorURLParams} from "@app/common/components/viewers/configurator/helpers/parameters"
import {ConnectionSolver} from "@app/template-editor/helpers/connection-solver/connection-solver"
import {DIALOG_DEFAULT_WIDTH} from "@app/template-editor/helpers/constants"
import {WebAssemblyWorkerService} from "@app/template-editor/helpers/mesh-processing"
import {MeshGeometryService} from "@app/template-editor/services/mesh-geometry.service"
import {cancelDeferredTask, isApple, isMobileDevice, queueDeferredTask} from "@cm/browser-utils"
import {DataResolver} from "@cm/material-nodes"
import {Matrix4} from "@cm/math"
import {DataNodes, MeshNodes} from "@cm/render-nodes"
import {CompactUIDTable, TypedImageData} from "@cm/utils"
import {DataObject} from "@cm/utils/data-object"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {BakeTextureCache} from "@template-editor/helpers/bake-texture-cache"
import {BakeTextureService} from "@template-editor/services/bake-texture.service"
import {ConfiguratorMaterialForSceneManagerGQL} from "@template-editor/services/scene-manager.service.generated"
import {catchError, defer, delay, EMPTY, filter, finalize, firstValueFrom, map, Observable, Subject, Subscription, switchMap, take, tap, timer} from "rxjs"
import {SceneManagerCacheService} from "@app/template-editor/services/scene-manager-cache.service"

const getEmptyTemplateGraph = () => new TemplateGraph({parameters: {name: "Empty Graph", nodes: new TemplateNodes({parameters: {list: []}})}})
const instantiateTemplateGraph = (templateGraph: TemplateGraph, parameters: Parameters, lockedTransform: Matrix4) =>
    new TemplateInstance({
        parameters: {
            name: "Root Template",
            id: "root",
            template: templateGraph,
            parameters,
            lockedTransform: lockedTransform.toArray(),
            visible: true,
        },
    })

export type SceneNodePartIntersection = {sceneNodePart: SceneNodePart; intersection?: Intersection}

export type SceneNodePartClickEvent = {
    target: SceneNodePartIntersection[]
    modifiers: {shiftKey: boolean; ctrlKey: boolean; metaKey: boolean}
}

const uvWireframeTestMaterial = new MaterialReference({parameters: {name: "Pebble gray", materialId: 14, materialRevisionId: 2390}})

const uv1TestMaterialUv0 = new MaterialReference({parameters: {name: "UV Test Pattern - Checker map B", materialId: 15851, materialRevisionId: 50260}})
const uv1TestMaterialUv1 = new MaterialReference({parameters: {name: "UV Test Pattern - Checker map B", materialId: 15851, materialRevisionId: 55414}})
const uv1TestMaterialUv2 = new MaterialReference({parameters: {name: "UV Test Pattern - Checker map B", materialId: 15851, materialRevisionId: 55415}})

const uv2TestMaterialUv0 = new MaterialReference({parameters: {name: "UV Test Pattern - Checker map Grain", materialId: 15852, materialRevisionId: 50261}})
const uv2TestMaterialUv1 = new MaterialReference({parameters: {name: "UV Test Pattern - Checker map Grain", materialId: 15852, materialRevisionId: 55416}})
const uv2TestMaterialUv2 = new MaterialReference({parameters: {name: "UV Test Pattern - Checker map Grain", materialId: 15852, materialRevisionId: 55417}})

export const objectPathPartEquals = (a: ObjectPathPart, b: ObjectPathPart) => {
    return a.part === b.part && a.path.length === b.path.length && objectPathStartsWith(a.path, b.path)
}

@Injectable()
export class SceneManagerService implements OnDestroy {
    //Inputs
    readonly $defaultCustomerId = signal<number | undefined>(undefined)
    readonly $templateRevisionId = signal<string | undefined>(undefined)
    readonly $templateId = signal<string | undefined>(undefined)
    readonly $transformMode = signal<TransformMode>(TransformMode.Translate)
    readonly $exposeClaimedSubTemplateInputs = signal(true)
    readonly $templateGraph = signal(getEmptyTemplateGraph())
    readonly $instanceParameters = signal(new Parameters({parameters: {}}))
    readonly $instanceTransform = signal(Matrix4.identity())
    readonly $lodType = signal<LodTypeValue>("web")
    readonly $reviewMode = signal<"wireframe" | "uvTest1" | "uvTest2" | undefined>(undefined)
    readonly $reviewFocus = signal<SceneNodes.WireframeMesh["channel"]>("faces")
    readonly $showDimensionGuides = signal(false)
    readonly $computeDummyReifiedMeshData = signal(false)
    readonly $computeDataObjectReferences = signal(false)

    //Undo / Redo related
    readonly $templateGraphChangeReference = signal<TemplateGraphSnapshot | undefined>(undefined)
    readonly $currentlyModifyingScene = computed(() => this.$templateGraphChangeReference() !== undefined)
    finishedModifyingScene$ = toObservable(this.$templateGraphChangeReference).pipe(
        filter((x) => x === undefined),
        map(() => {
            return
        }),
    )
    private readonly $undoHistory = signal<TemplateGraphDifferences[]>([])
    private readonly $redoHistory = signal<TemplateGraphDifferences[]>([])
    readonly $canUndo = computed(() => this.$undoHistory().length > 0 && this.$templateGraphChangeReference() === undefined)
    readonly $canRedo = computed(() => this.$redoHistory().length > 0 && this.$templateGraphChangeReference() === undefined)

    //Current configuration
    readonly $currentLocalConfiguration = computed(() => this.getCurrentConfiguration(false), {
        equal: (a, b) => a.getHash() === b.getHash(),
    })
    readonly $currentGlobalConfiguration = computed(() => this.getCurrentConfiguration(true), {
        equal: (a, b) => a.getHash() === b.getHash(),
    })

    //Events
    private _templateTreeChanged$ = new Subject<TemplateGraphDifferences>()
    public templateTreeChanged$ = this._templateTreeChanged$.asObservable()
    private _templateSwapped$ = new Subject<void>()
    public templateSwapped$ = this._templateSwapped$.asObservable()

    //Modified state
    private readonly $updatedSerializedTemplateGraphSignal = signal(0)
    synchronizeSerializedTemplateGraph() {
        this.$updatedSerializedTemplateGraphSignal.update((x) => x + 1)
    }
    private readonly $serializedTemplateGraphHash = computed(() => {
        this.$updatedSerializedTemplateGraphSignal()
        const templateGraph = this.$templateGraph()
        return templateGraph.getHash()
    })
    private readonly $templateTreeChangedSignal = toSignal(this.templateTreeChanged$)
    private readonly $currentTemplateGraphHash = computed(() => {
        this.$templateTreeChangedSignal()
        const templateGraph = this.$templateGraph()
        return templateGraph.getHash()
    })
    readonly $templateGraphModified = computed(() => this.$serializedTemplateGraphHash() !== this.$currentTemplateGraphHash())

    private _clickedSceneNodePart$ = new Subject<SceneNodePartClickEvent>()
    private clickedSceneNodePart$ = this._clickedSceneNodePart$.asObservable()
    readonly $watchingForClickedSceneNodePart = signal(false)
    watchForClickedSceneNodePart() {
        if (this.$watchingForClickedSceneNodePart()) throw new Error("Already watching for clicked scene node part")

        this.$watchingForClickedSceneNodePart.set(true)
        return this.clickedSceneNodePart$.pipe(
            take(1),
            finalize(() => this.$watchingForClickedSceneNodePart.set(false)),
        )
    }

    readonly $templateInstance = computed(() => instantiateTemplateGraph(this.$templateGraph(), this.$instanceParameters(), this.$instanceTransform()))

    private readonly $preDisplayList = signal<SceneNodes.SceneNode[]>([])
    private readonly $displayList = signal<SceneNodes.SceneNode[]>([])
    public readonly $descriptorList = signal<InterfaceDescriptor<unknown, object>[]>([])
    private readonly $infoList = signal<InfoNodes.InfoNode[]>([])
    descriptorList$ = toObservable(this.$descriptorList)
    private readonly $_ready = signal<boolean>(false)
    public $ready = this.$_ready.asReadonly()
    ready$ = toObservable(this.$_ready)
    templateRevisionId$ = toObservable(this.$templateRevisionId)
    defaultCustomerId$ = toObservable(this.$defaultCustomerId)
    templateGraph$ = toObservable(this.$templateGraph)

    readonly $scene = computed(() => [...this.$preDisplayList(), ...this.$displayList()])

    readonly $cameras = computed(() => this.$scene().filter(SceneNodes.Camera.is))

    readonly $reviewedUvChannel = computed(() => {
        const reviewFocus = this.$reviewFocus()
        if (reviewFocus === "faces") return "uv0"
        else return reviewFocus
    })

    private readonly $_selectedObjectPathParts = signal<ObjectPathPart[]>([])
    public $selectedObjectPathParts = this.$_selectedObjectPathParts.asReadonly()
    public selectedObjectPathParts$ = toObservable(this.$selectedObjectPathParts)

    private getRootPartsForObjectPathPart(objectPathParts: ObjectPathPart[]): TemplateNodePart[] {
        const result = objectPathParts.map((objectPathPart) => ({
            templateNode: getRootTemplateNodeForObjectPath(objectPathPart.path),
            part: objectPathPart.path.length === 1 ? objectPathPart.part : "root",
        }))
        return result.filter((part, index) => {
            return index === result.findIndex((p) => p.templateNode === part.templateNode && p.part === part.part)
        })
    }

    public $selectedRootNodeParts = computed<TemplateNodePart[]>(() => {
        const selectedObjectPathParts = this.$selectedObjectPathParts()
        return this.getRootPartsForObjectPathPart(selectedObjectPathParts)
    })
    public selectedRootNodeParts$ = toObservable(this.$selectedRootNodeParts)

    readonly $_hoveredObjectPathParts = signal<ObjectPathPart[]>([])
    readonly $hoveredObjectPathParts = this.$_hoveredObjectPathParts.asReadonly()
    readonly hoveredObjectPathParts$ = toObservable(this.$hoveredObjectPathParts)

    private readonly $_highlightedNodes = signal<TemplateNode[]>([])
    $highlightedNodes = this.$_highlightedNodes.asReadonly()

    private readonly $_compilationContextOtherData = signal<ContextOtherData>({})
    $compilationContextOtherData = this.$_compilationContextOtherData.asReadonly()
    setCompilationContextOtherDataEntry(key: string, value: unknown) {
        this.$_compilationContextOtherData.update((old) => {
            if (old[key] === value) return old
            return {
                ...old,
                [`${key}`]: value,
            }
        })
    }

    private readonly $_criticalTasks = signal<SceneManagerTask[]>([])
    criticalTasks$ = toObservable(this.$_criticalTasks)
    private readonly $_optionalTasks = signal<SceneManagerTask[]>([])
    optionalTasks$ = toObservable(this.$_optionalTasks)
    readonly $numCriticalTasks = computed(() => this.$_criticalTasks().length)
    readonly $numPendingTasks = computed(() => this.$numCriticalTasks() + this.$_optionalTasks().length)

    private readonly $activeNodeSet = signal(new Set<TemplateNode>())
    private readonly $nodeToInfoMap = signal(new Map<TemplateNode, InfoNodeId>())
    private readonly $solverUpdateAll = signal<(postStep: boolean) => void>(() => {})
    private readonly $transformAccessorMap = signal<Map<TemplateNode, TransformAccessor>>(new Map())
    private readonly $liveValueAccessorMap = signal<Map<TemplateNode, LiveValueAccessor<unknown>>>(new Map())

    readonly workerService = inject(WebAssemblyWorkerService)
    private readonly meshGeometryService = inject(MeshGeometryService)
    private readonly bakeTextureService = inject(BakeTextureService)
    private readonly sceneManagerCacheService = inject(SceneManagerCacheService)
    private readonly destroyRef = inject(DestroyRef)
    private readonly dialog = inject(MatDialog)
    private sceneManager: SceneManager
    private readonly logger: CmLogger = inject(CM_LOGGER_SCENE)
    private scheduler = new GraphScheduler(undefined, {cancelDeferredTask, queueDeferredTask}, this.logger)
    private builder = new GraphBuilder("root", this.scheduler, this.logger)
    private readonly injector = inject(Injector)

    readonly $configuratorUrlParams = computed(() => {
        return encodeConfiguratorURLParams(this.$descriptorList(), this.injector)
    })

    readonly $sceneOptions = computed(() => {
        return this.$scene().find(SceneNodes.SceneOptions.is)
    })

    readonly $verticalArPlacement = computed(() => {
        return this.$sceneOptions()?.verticalArPlacement ?? false
    })

    readonly $pdfSettings = computed(() => {
        return [...this.$activeNodeSet()].find((n) => n instanceof PdfSettings)
    })

    private requestedRecompile = new Subject<void>()

    constructor() {
        effect(() => this.compileTemplate())

        effect(() => {
            this.$scene()

            const solver = this.sceneManager.getConnectionSolver()
            if (solver.checkReady()) this.updateAllMeshPositions(solver)
            else this.logger.warn("Solver not ready")
        })

        effect(() => {
            this.$templateGraph()
            untracked(() => this._templateSwapped$.next())
        })

        this.templateSwapped$.pipe(takeUntilDestroyed(this.destroyRef), delay(0)).subscribe(() => {
            this.$templateGraphChangeReference.set(undefined)
            this.$undoHistory.set([])
            this.$redoHistory.set([])

            this.$_selectedObjectPathParts.set([])
            this.hoverNodes(undefined)
            this.$_highlightedNodes.set([])
        })

        this.requestedRecompile
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(() => timer(100)),
            )
            .subscribe(() => this.compileTemplate())

        this.sceneManager = new SceneManager(
            this,
            this.sceneManagerCacheService,
            this.meshGeometryService,
            this.bakeTextureService,
            this.$templateInstance,
            this.$currentlyModifyingScene,
            this.finishedModifyingScene$,
        )

        this.workerService.startInitialWorkers()
    }

    getSceneNodes(templateNode: TemplateNode): SceneNodes.SceneNode[] {
        const scene = this.$scene()
        return scene.filter((sceneNode) => {
            return sceneNode.path.includes(templateNode)
        })
    }

    getInfoNodeId(node: TemplateNode) {
        return this.$nodeToInfoMap().get(node)
    }

    getInfoNodeParts(nodePart: TemplateNodePart): InfoNodePart[] {
        const {templateNode, part} = nodePart

        const infoNodeId = this.getInfoNodeId(templateNode)
        if (!infoNodeId) return []

        return this.$infoList()
            .filter((infoNode) => infoNode.id === infoNodeId)
            .map((infoNode) => ({infoNode, part}))
    }

    getPartForSelectedNode(node: TemplateNode): TemplateNodePart["part"] | undefined {
        return this.$selectedRootNodeParts().find((part) => part.templateNode === node)?.part
    }

    compileTemplate(): void {
        const templateInstance = this.$templateInstance()

        const scope = this.builder.scope()

        const templateMatrix = this.$instanceTransform()

        const templateContext: TemplateContext = {
            sceneManager: this.sceneManager,
            templateMatrix,
            lodType: this.$lodType(),
            computeDummyReifiedMeshData: this.$computeDummyReifiedMeshData(),
            computeDataObjectReferences: this.$computeDataObjectReferences(),
            defaultCustomerId: this.$defaultCustomerId(),
            showDimensions: this.$showDimensionGuides(),
            instanceIdChain: [templateInstance.parameters.id],
            logger: this.logger,
        }

        const evaluator = new NodeEvaluator(new CompactUIDTable<string>(), scope, templateContext, {}, undefined)
        const [graphValid, graphInvalid] = scope.branch(evaluator.evaluate(templateInstance.parameters.template))
        const graph = scope.phi(scope.get(graphValid, "graph"), graphInvalid)

        const [inputs] = evaluator.evaluateTemplateInputs(scope, templateInstance)

        const overrideMaterial = (() => {
            switch (this.$reviewMode()) {
                case "wireframe":
                    return uvWireframeTestMaterial
                case "uvTest1":
                    switch (this.$reviewedUvChannel()) {
                        case "uv0":
                            return uv1TestMaterialUv0
                        case "uv1":
                            return uv1TestMaterialUv1
                        case "uv2":
                            return uv1TestMaterialUv2
                    }
                    break
                case "uvTest2":
                    switch (this.$reviewedUvChannel()) {
                        case "uv0":
                            return uv2TestMaterialUv0
                        case "uv1":
                            return uv2TestMaterialUv1
                        case "uv2":
                            return uv2TestMaterialUv2
                    }
                    break
                default:
                    return undefined
            }
        })()

        const contextOtherData = this.$compilationContextOtherData()

        const compileTemplate = scope.node(CompileTemplate, {
            graph,
            sceneProperties: null,
            render: null,
            environment: null,
            inputs,
            templateContext,
            path: [],
            compiledTemplateId: null,
            templateDepth: 0,
            exposeClaimedSubTemplateInputs: this.$exposeClaimedSubTemplateInputs(),
            overrideMaterial: () => overrideMaterial,
            contextOtherData: contextOtherData,
        })

        const runTemplate = scope.node(RunTemplate, {
            compiledTemplate: compileTemplate.compiledTemplate,
        })

        const solver = scope.node(Solver, {
            state: scope.state(SolverState, "solverState"),
            sceneManager: this.sceneManager,
            data: runTemplate.solverData,
        })

        scope.tap(compileTemplate.activeNodeSet, (activeNodeSet) => {
            if (activeNodeSet !== NotReady) this.$activeNodeSet.set(activeNodeSet)
        })

        scope.tap(runTemplate.nodeToInfoMap, (nodeToInfoMap) => {
            if (nodeToInfoMap !== NotReady) this.$nodeToInfoMap.set(nodeToInfoMap)
        })

        scope.tap(runTemplate.preDisplayList, (preDisplayList) => {
            if (preDisplayList !== NotReady) this.$preDisplayList.set(preDisplayList)
        })

        scope.tap(runTemplate.displayList, (displayList) => {
            if (displayList !== NotReady) this.$displayList.set(displayList)
        })

        scope.tap(runTemplate.descriptorList, (descriptorList) => {
            if (descriptorList !== NotReady) this.$descriptorList.set(descriptorList)
        })

        scope.tap(runTemplate.infoList, (infoList) => {
            if (infoList !== NotReady) this.$infoList.set(infoList)
        })

        scope.tap(runTemplate.ready, (ready) => {
            if (ready !== NotReady) this.$_ready.set(ready)
            else this.$_ready.set(false)
        })

        scope.tap(runTemplate.transformAccessorList, (transformAccessorList) => {
            if (transformAccessorList !== NotReady)
                this.$transformAccessorMap.set(new Map(transformAccessorList.map((x) => [x.templateNode, x.transformAccessor])))
        })

        scope.tap(runTemplate.liveValueAccessorList, (liveValueAccessorList) => {
            if (liveValueAccessorList !== NotReady) this.$liveValueAccessorMap.set(new Map(liveValueAccessorList.map((x) => [x.templateNode, x.accessor])))
        })

        scope.tap(solver.updateAll, (updateAll) => {
            if (updateAll !== NotReady) this.$solverUpdateAll.set(updateAll)
        })

        this.builder.finalizeChanges()
    }

    ngOnDestroy(): void {
        this.sceneManager.destroy()
    }

    requiresSync(waitForOptionalTasks: boolean) {
        return (
            this.scheduler.hasPendingUpdates ||
            this.$_criticalTasks().length > 0 ||
            (waitForOptionalTasks && this.$_optionalTasks().length > 0) ||
            !this.$ready()
        )
    }

    async sync(waitForOptionalTasks: boolean): Promise<void> {
        if (this.scheduler.hasPendingUpdates) {
            await firstValueFrom(this.scheduler.sync())
            return this.sync(waitForOptionalTasks)
        } else if (this.$_criticalTasks().length > 0) {
            await firstValueFrom(
                this.criticalTasks$.pipe(
                    filter((criticalTasks) => criticalTasks.length === 0),
                    delay(0), //This is essential, otherwise the signal will not have been updated after the await
                    take(1),
                ),
            )
            return this.sync(waitForOptionalTasks)
        } else if (waitForOptionalTasks && this.$_optionalTasks().length > 0) {
            await firstValueFrom(
                this.optionalTasks$.pipe(
                    filter((optionalTasks) => optionalTasks.length === 0),
                    delay(0), //This is essential, otherwise the signal will not have been updated after the await
                    take(1),
                ),
            )
            return this.sync(waitForOptionalTasks)
        } else if (!this.$ready()) {
            await firstValueFrom(
                this.ready$.pipe(
                    filter((ready) => ready === true),
                    take(1),
                ),
            )
            return this.sync(waitForOptionalTasks)
        }
    }

    selectNode(objectPathPart: ObjectPathPart | undefined) {
        this.selectNodes(objectPathPart ? [objectPathPart] : [])
    }

    setTemplateParameter(paramId: string, value: TemplateParameterValue | undefined, compileTemplate = true) {
        const instanceParameters = this.$instanceParameters()

        instanceParameters.replaceParameters({
            ...instanceParameters.parameters,
            [paramId]: value,
        })
        if (compileTemplate) {
            this.compileTemplate()
        }
    }

    clearParameters(compileTemplate = true) {
        this.$instanceParameters().replaceParameters({})
        if (compileTemplate) {
            this.compileTemplate()
        }
    }

    addNodeToSelection(objectPathPart: ObjectPathPart) {
        const selectedObjectPathParts = this.$selectedObjectPathParts()
        if (!selectedObjectPathParts.some((x) => objectPathPartEquals(x, objectPathPart))) {
            this.$_selectedObjectPathParts.set([...selectedObjectPathParts, objectPathPart])
        }
    }

    selectNodes(objectPathParts: ObjectPathPart[]) {
        const newObjectPathParts = objectPathParts.filter((part, index) => index === objectPathParts.findIndex((p) => objectPathPartEquals(p, part)))
        const currentObjectPathParts = this.$selectedObjectPathParts()
        const unchanged =
            newObjectPathParts.length === currentObjectPathParts.length &&
            newObjectPathParts.every((newPart) => currentObjectPathParts.some((currentPart) => objectPathPartEquals(currentPart, newPart)))

        if (newObjectPathParts.length === 1) {
            const objectPathPart = newObjectPathParts[0]
            const templateNode = getRootTemplateNodeForObjectPath(objectPathPart.path)
            if (unchanged) {
                const {path, part} = objectPathPart
                if (templateNode instanceof TargetableObjectNode && (templateNode as TargetableObjectNode).parameters.targeted && part === "root") {
                    this.$_selectedObjectPathParts.set([{path, part: "target"}])
                }
                return
            }
            if (templateNode instanceof ConfigVariant) {
                const configVariant = templateNode
                getNodeOwner(configVariant, (nodeOwner) => {
                    if (nodeOwner instanceof ConfigGroup) {
                        const instanceParameters = this.$instanceParameters()
                        instanceParameters.replaceParameters({
                            ...instanceParameters.parameters,
                            [nodeOwner.parameters.id]: configVariant.parameters.id,
                        })
                        this.compileTemplate()
                    }
                })
            }
        } else if (unchanged) return

        this.$_selectedObjectPathParts.set(newObjectPathParts)
    }

    removeNodeFromSelection(objectPathPart: ObjectPathPart) {
        const selectedObjectPathParts = this.$selectedObjectPathParts()
        const selectedNodePartIdx = selectedObjectPathParts.findIndex((x) => objectPathPartEquals(x, objectPathPart))
        if (selectedNodePartIdx >= 0) {
            selectedObjectPathParts.splice(selectedNodePartIdx, 1)
            this.$_selectedObjectPathParts.set([...selectedObjectPathParts])
        }
    }

    handleClickEvent(event: SceneNodePartClickEvent) {
        if (this.$watchingForClickedSceneNodePart()) {
            this._clickedSceneNodePart$.next(event)
            return
        }

        const {target, modifiers} = event
        const {shiftKey, ctrlKey, metaKey} = modifiers

        const toggleMode = isApple ? metaKey : ctrlKey

        if (target.length > 0) {
            const {sceneNodePart} = target[0]
            const objectPathPart = {path: sceneNodePart.sceneNode.path, part: sceneNodePart.part}
            if (objectPathPart.path.length === 0) return
            const {path, part} = objectPathPart

            const proceduralObjectPathPart = ((): ObjectPathPart | undefined => {
                const lastProceduralNode = path.findLast(isProceduralTemplateNode)
                if (lastProceduralNode) {
                    const resolvedSubPath = lastProceduralNode.getParameterNodePartForSceneNodePart(sceneNodePart, {
                        getInfoNodeParts: (nodePart: TemplateNodePart) => this.getInfoNodeParts(nodePart),
                    })

                    if (resolvedSubPath) {
                        const pathPrefix = path.slice(0, path.indexOf(lastProceduralNode))
                        return {path: [...pathPrefix, lastProceduralNode, ...resolvedSubPath.path], part: isControlPointPart(part) ? part : "root"}
                    }
                }

                return undefined
            })()

            const extraParts: ObjectPathPart[] = proceduralObjectPathPart ? [objectPathPart, proceduralObjectPathPart] : [objectPathPart]
            const selectedObjectPathParts = this.$selectedObjectPathParts()
            if (toggleMode) {
                const existing = selectedObjectPathParts.find((selectedObjectPathPart) => objectPathPartEquals(selectedObjectPathPart, objectPathPart))
                if (existing) {
                    this.selectNodes(selectedObjectPathParts.filter((x) => !extraParts.some((r) => objectPathPartEquals(x, r))))
                } else {
                    this.selectNodes([...selectedObjectPathParts, ...extraParts])
                }
            } else if (shiftKey) {
                this.selectNodes([...selectedObjectPathParts, ...extraParts])
            } else {
                this.selectNodes(extraParts)
            }
        } else this.selectNode(undefined)
    }

    hoverNodes(objectPathParts: ObjectPathPart[] | undefined) {
        const newObjectPathParts = objectPathParts ?? []
        const currentObjectPathParts = this.$hoveredObjectPathParts()
        if (
            newObjectPathParts.length === currentObjectPathParts.length &&
            newObjectPathParts.every((newPart) => currentObjectPathParts.some((currentPart) => objectPathPartEquals(currentPart, newPart)))
        ) {
            return
        }

        this.$_hoveredObjectPathParts.set(newObjectPathParts)
    }

    highlightNode(templateNode: TemplateNode, value: boolean) {
        const highlightedNodes = this.$highlightedNodes()

        if (value) {
            if (!highlightedNodes.includes(templateNode)) this.$_highlightedNodes.set([...highlightedNodes, templateNode])
        } else {
            const idx = highlightedNodes.indexOf(templateNode)
            if (idx >= 0) {
                highlightedNodes.splice(idx, 1)
                this.$_highlightedNodes.set([...highlightedNodes])
            }
        }
    }

    isHighlightedNode(templateNode: TemplateNode) {
        return this.$highlightedNodes().includes(templateNode)
    }

    addTask(sceneManagerTask: SceneManagerTask): Subscription {
        const {description, task, critical} = sceneManagerTask

        const tasksSignal = critical ? this.$_criticalTasks : this.$_optionalTasks

        tasksSignal.update((existingTasks) => [...existingTasks, sceneManagerTask])
        const taskOperator = defer(() => {
            const startTime = Date.now()
            return task.pipe(
                tap(() => {
                    const endTime = Date.now()
                    const timeDiff = endTime - startTime
                    this.logger.log(`Task ${description} took ${timeDiff}ms`)
                }),
                catchError((error) => {
                    const endTime = Date.now()
                    const timeDiff = endTime - startTime
                    this.logger.error(`Task ${description} failed after ${timeDiff}ms`, error)
                    return EMPTY
                }),
                finalize(() => {
                    tasksSignal.update((existingTasks) => {
                        return existingTasks.filter((x) => x !== sceneManagerTask)
                    })
                }),
            )
        })

        if (critical) {
            return taskOperator.subscribe()
        } else {
            return this.criticalTasks$
                .pipe(
                    filter((criticalTasks) => criticalTasks.length === 0),
                    take(1),
                    tap(() => this.logger.debug("Critical tasks completed, starting optional task", description)),
                    switchMap(() => taskOperator),
                )
                .subscribe()
        }
    }

    isNodeActive(node: TemplateNode) {
        return this.$activeNodeSet().has(node)
    }

    private updateAllMeshPositions(solver: ConnectionSolver) {
        const solverUpdateAll = this.$solverUpdateAll()

        solverUpdateAll(false)
        while (solver.step()) {
            // Intentionally empty
        }
        solverUpdateAll(true)

        return true
    }

    getISceneManager() {
        return this.sceneManager
    }

    getTransformAccessor(node: TemplateNode) {
        return this.$transformAccessorMap().get(node)
    }

    getLiveValueAccessor<T>(node: TemplateNode): LiveValueAccessor<T> | undefined {
        return this.$liveValueAccessorMap().get(node) as LiveValueAccessor<T> | undefined
    }

    beginModifyTemplateGraph() {
        const templateGraphChangeReference = this.$templateGraphChangeReference()
        if (templateGraphChangeReference) return

        this.$templateGraphChangeReference.set(this.$templateGraph().getSnapshot())
    }

    endModifyTemplateGraph() {
        const templateGraphChangeReference = this.$templateGraphChangeReference()
        if (!templateGraphChangeReference) return

        const templateGraph = this.$templateGraph()
        const graphDifference = templateGraph.getDifferences(templateGraphChangeReference)
        this.notifyTemplateTreeChange(graphDifference)
        this.pushUndoHistory(graphDifference)

        this.$templateGraphChangeReference.set(undefined)
    }

    private pushUndoHistory(graphDifference: TemplateGraphDifferences) {
        const undoHistory = this.$undoHistory()
        undoHistory.push(graphDifference)
        if (undoHistory.length > 20) undoHistory.shift()
        this.$undoHistory.set([...undoHistory])
        this.$redoHistory.set([])
    }

    private notifyTemplateTreeChange(differences: TemplateGraphDifferences) {
        const {deletedNodes, modifiedNodes} = differences

        if (deletedNodes.size > 0) {
            const selectedObjectPathParts = this.$selectedObjectPathParts()
            const selectedNodePartsFiltered = selectedObjectPathParts.filter((x) => x.path.every((y) => !deletedNodes.has(y)))
            if (selectedNodePartsFiltered.length !== selectedObjectPathParts.length) this.$_selectedObjectPathParts.set(selectedNodePartsFiltered)

            const hoveredNodePart = this.$hoveredObjectPathParts()
            const hoveredNodePartsFiltered = hoveredNodePart.filter((x) => x.path.every((y) => !deletedNodes.has(y)))
            if (hoveredNodePartsFiltered.length !== hoveredNodePart.length) this.$_hoveredObjectPathParts.set(hoveredNodePartsFiltered)

            const highlightedNodes = this.$highlightedNodes()
            const highlightedNodesFiltered = highlightedNodes.filter((x) => !deletedNodes.has(x))
            if (highlightedNodesFiltered.length !== highlightedNodes.length) this.$_highlightedNodes.set(highlightedNodesFiltered)
        }

        if (modifiedNodes.size > 0) {
            const selectedObjectPathParts = this.$selectedObjectPathParts()
            const selectionChanged = selectedObjectPathParts.find((x) => !x.path.every((y) => modifiedNodes.has(y))) !== undefined
            if (selectionChanged) this.$_selectedObjectPathParts.set([...selectedObjectPathParts])

            const hoveredNodePart = this.$hoveredObjectPathParts()
            const hoveredChanged = hoveredNodePart.find((x) => !x.path.every((y) => modifiedNodes.has(y))) !== undefined
            if (hoveredChanged) this.$_hoveredObjectPathParts.set([...hoveredNodePart])

            const highlightedNodes = this.$highlightedNodes()
            const highlightedNodesChanged = [...highlightedNodes].find((x) => modifiedNodes.has(x)) !== undefined
            if (highlightedNodesChanged) this.$_highlightedNodes.set([...highlightedNodes])
        }

        this._templateTreeChanged$.next(differences)
    }

    private modifyTemplateGraphImpl(modifier: (templateGraph: TemplateGraph) => void, undoable: boolean, compileImmediate: boolean) {
        const templateGraph = this.$templateGraph()
        const templateGraphChangeReference = this.$templateGraphChangeReference()

        if (!templateGraphChangeReference) {
            const graphDifference = templateGraph.trackDifferences(modifier)
            const {addedNodes, deletedNodes, modifiedNodes} = graphDifference
            if (addedNodes.size === 0 && deletedNodes.size === 0 && modifiedNodes.size === 0) return
            this.notifyTemplateTreeChange(graphDifference)
            if (undoable) this.pushUndoHistory(graphDifference)
        } else modifier(templateGraph)

        if (compileImmediate) this.compileTemplate()
        else this.requestedRecompile.next()
    }

    modifyTemplateGraph(modifier: (templateGraph: TemplateGraph) => void, compileImmediate = true, undoable = true) {
        this.modifyTemplateGraphImpl(modifier, undoable, compileImmediate)
    }

    undo = () => {
        if (!this.$canUndo()) return

        const undoHistory = this.$undoHistory()
        const redoHistory = this.$redoHistory()

        const graphDifference = undoHistory[undoHistory.length - 1]
        if (graphDifference) {
            this.modifyTemplateGraphImpl(() => graphDifference.applyBackward(), false, true)
            undoHistory.pop()

            redoHistory.push(graphDifference)
            this.$undoHistory.set([...undoHistory])
            this.$redoHistory.set([...redoHistory])
        }
    }

    redo = () => {
        if (!this.$canRedo()) return

        const undoHistory = this.$undoHistory()
        const redoHistory = this.$redoHistory()

        if (redoHistory.length === 0) return

        const graphDifference = redoHistory[redoHistory.length - 1]
        if (graphDifference) {
            this.modifyTemplateGraphImpl(() => graphDifference.applyForward(), false, true)
            redoHistory.pop()

            undoHistory.push(graphDifference)
            this.$undoHistory.set([...undoHistory])
            this.$redoHistory.set([...redoHistory])
        }
    }

    deleteNodes(templateNodeParts: TemplateNodePart[]) {
        const selectedControlPoints = templateNodeParts.filter(
            (
                templateNodePart,
            ): templateNodePart is {
                templateNode: MeshCurve
                part: `controlPoint${number}`
            } => templateNodePart.templateNode instanceof MeshCurve && isControlPointPart(templateNodePart.part),
        )
        const otherTemplateNodeParts = templateNodeParts.filter(
            (templateNodePart) => !selectedControlPoints.some((selectedControlPoint) => selectedControlPoint === templateNodePart),
        )

        const nodesToDelete = new Set([...otherTemplateNodeParts.map((x) => x.templateNode)])

        if (nodesToDelete.size === 0 && selectedControlPoints.length === 0) return

        const templateGraph = this.$templateGraph()

        if (nodesToDelete.has(templateGraph)) return

        const deleteNodes = () => {
            this.modifyTemplateGraph((templateGraph) => {
                const selectedControlPointsByTemplateNode = new Map<MeshCurve, Set<number>>()
                for (const selectedControlPoint of selectedControlPoints) {
                    const {templateNode, part} = selectedControlPoint
                    const index = getControlPointPartNumber(part)
                    const controlPoints = selectedControlPointsByTemplateNode.get(templateNode) ?? new Set()
                    controlPoints.add(index)
                    selectedControlPointsByTemplateNode.set(templateNode, controlPoints)
                }
                for (const [templateNode, controlPoints] of selectedControlPointsByTemplateNode)
                    templateNode.updateParameters({
                        controlPoints: [...templateNode.parameters.controlPoints].filter(
                            (_, index) => !controlPoints.has(index),
                        ) as typeof templateNode.parameters.controlPoints,
                    })

                deleteNodesFromTemplateGraph(templateGraph, nodesToDelete, this.logger)
            })
        }

        const referencingNodes = getReferencingDeletedTemplateNodes(templateGraph, nodesToDelete)
        if (referencingNodes.size === 0) deleteNodes()
        else {
            const describeNode = (node: TemplateNode, isReference: boolean): string => {
                if (isTemplateContainer(node)) {
                    const containerParent = getUniqueTemplateNodeParent(node)
                    return getTemplateNodeLabel(containerParent)
                } else if (node instanceof MaterialAssignments) {
                    const parentMesh = getUniqueTemplateNodeParent(node)
                    return getTemplateNodeLabel(parentMesh)
                } else if (node instanceof MaterialAssignment) {
                    if (isReference) return getTemplateNodeLabel(node.parameters.node)
                    else return `${getTemplateNodeLabel(node)} of ${describeNode(getUniqueTemplateNodeParent(node), false)} `
                } else if (node instanceof Parameters) {
                    const parentMesh = getUniqueTemplateNodeParent(node)
                    return getTemplateNodeLabel(parentMesh)
                } else if (node instanceof OutputNode) {
                    if (isReference) return getTemplateNodeLabel(node.parameters.template)
                    else return `${getTemplateNodeLabel(node)} of ${describeNode(getUniqueTemplateNodeParent(node), false)} `
                }

                return getTemplateNodeLabel(node)
            }

            const count = [...referencingNodes].reduce((acc, [, children]) => acc + children.size, 0)
            const description = [...referencingNodes].reduce((acc, [node, children]) => {
                const addNewLine = acc.length > 0 ? acc + "<br><br>" : acc
                return (
                    addNewLine +
                    "<strong>" +
                    describeNode(node, false) +
                    "</strong>" +
                    " references " +
                    [...new Set(children.values())].reduce((acc, child, index) => {
                        const addComma = acc.length > 0 ? (index === children.size - 1 ? acc + " and " : acc + ", ") : acc
                        return addComma + "<strong>" + describeNode(child, true) + "</strong>"
                    }, "")
                )
            }, "")

            const dialogRef: MatDialogRef<DialogComponent, boolean> = this.dialog.open(DialogComponent, {
                width: DIALOG_DEFAULT_WIDTH,
                data: {
                    title: "Delete Nodes",
                    message:
                        "There are <strong>" +
                        count.toString() +
                        "</strong> references to the nodes about to be deleted: <br><br>" +
                        description +
                        "<br><br>Those references will also be removed in the process. The nodes themselves will not be deleted.<br><br>Are you sure you want to proceed?",
                    confirmLabel: "Delete References",
                    cancelLabel: "Cancel",
                    isDestructive: true,
                },
            })
            dialogRef.afterClosed().subscribe((confirmed) => {
                if (!confirmed) return
                deleteNodes()
            })
        }
    }

    getDescriptorsForNode(node: TemplateInstance) {
        const descriptorList = this.$descriptorList()
        if (node === this.$templateInstance()) return descriptorList
        return descriptorList.filter((descriptor) => {
            const [prefix, _] = getInterfaceIdPrefix(descriptor.props.id)
            return prefix === node.parameters.id
        })
    }

    /*This is only for configurations determined by config variants. Everything else
    messes up the rendering job assignments.*/
    private getCurrentConfiguration(includeAllSubTemplateInputs: boolean) {
        const configuration: {[paramId: string]: AnyJsonValue} = {}

        for (const descriptor of this.$descriptorList()) {
            if (descriptor.props.type !== "input") continue
            if (descriptor.props.isSetByTemplate && !includeAllSubTemplateInputs) continue

            const {id} = descriptor.props
            if (descriptor.props.value === null) continue

            if (descriptor instanceof ConfigInfo) configuration[id] = descriptor.props.value.id
            else if (
                descriptor instanceof ObjectInfo ||
                descriptor instanceof MeshInfo ||
                descriptor instanceof CurveInfo ||
                descriptor instanceof MaterialInfo ||
                descriptor instanceof TemplateInfo ||
                descriptor instanceof ImageInfo ||
                descriptor instanceof StringInfo ||
                descriptor instanceof NumberInfo ||
                descriptor instanceof BooleanInfo ||
                descriptor instanceof JsonInfo ||
                descriptor instanceof NodesInfo
            ) {
                continue
            } else throw new Error("Invalid descriptor type")
        }

        return new Parameters({parameters: configuration})
    }

    getDescriptors() {
        return this.$descriptorList()
    }

    reloadLatestMaterialRevision(materialId: number, latestRevisionLegacyId: number) {
        this.sceneManager.reloadLatestMaterialRevision(materialId, latestRevisionLegacyId)
    }

    async loadEvaluationResources(dataObjectReferences: DataNodes.DataObjectReference[]): Promise<DataNodes.EvaluationResources> {
        const entries = await Promise.all(
            dataObjectReferences.map(async ({dataObjectId}) => {
                const dataObject = await this.sceneManager.getResolver().dataObjectLegacy(dataObjectId)
                return [dataObjectId, {contentType: dataObject.mediaType, data: await this.sceneManager.loadDataObjectContent(dataObject)}] as const
            }),
        )
        return Object.fromEntries(entries)
    }
}

class SceneManager implements ISceneManager {
    private connectionSolver = new ConnectionSolver()

    private bakeTextureCache: BakeTextureCache
    private geometryBuilderContext: GeomBuilderContext | null = null

    private readonly configuratorMaterialForSceneManager = inject(ConfiguratorMaterialForSceneManagerGQL)
    private readonly logger: CmLogger = inject(CM_LOGGER_SCENE)

    constructor(
        private sceneManagerService: SceneManagerService,
        private sceneManagerCacheService: SceneManagerCacheService,
        private meshGeometryService: MeshGeometryService,
        private bakeTextureService: BakeTextureService,
        private templateInstance: Signal<TemplateInstance>,
        private readonly $currentlyModifyingScene: Signal<boolean>,
        private finishedModifyingScene$: Observable<void>,
    ) {
        this.bakeTextureCache = new BakeTextureCache(this.bakeTextureService, this)
    }

    destroy() {
        this.connectionSolver.destroy()
    }

    getConnectionSolver() {
        return this.connectionSolver
    }

    isMobileDevice() {
        return isMobileDevice
    }

    arMode() {
        return this.sceneManagerService.$lodType() === "ar"
    }

    defaultTransformForObject(node: ObjectNode, useLockedTransform: boolean = true): Matrix4 | undefined {
        if (useLockedTransform && isNodeWithMatrixArrayLockedTransformParameter(node)) {
            return Matrix4.fromArray(node.parameters.lockedTransform)
        } else if (node instanceof StoredMesh) {
            const {metaData} = node.parameters
            const {defaultPosition} = metaData ?? {}
            if (defaultPosition) {
                const [x, y, z] = defaultPosition
                return Matrix4.translation(x, y, z)
            }
        }

        return undefined
    }

    getRootNode() {
        return this.templateInstance()
    }

    addTask(sceneManagerTask: SceneManagerTask): Subscription {
        return this.sceneManagerService.addTask(sceneManagerTask)
    }

    async loadDataObjectContent(dataObject: DataObject): Promise<Uint8Array> {
        return firstValueFrom(this.sceneManagerCacheService.dataObjectContentCache.evaluateDataObject(dataObject))
    }

    async getDataObjectSize(dataObject: DataObject): Promise<number | null> {
        return firstValueFrom(this.sceneManagerCacheService.dataObjectSizeCache.evaluateDataObjectSize(dataObject))
    }

    async getDataObjectImageData(dataObject: DataObject): Promise<TypedImageData> {
        return firstValueFrom(this.sceneManagerCacheService.imageDataCache.evaluateImageData(dataObject))
    }

    async findMaterial(articleId: string | null, customerId: number | null, flatten: boolean) {
        const {configuratorMaterial} = await fetchThrowingErrors(this.configuratorMaterialForSceneManager)({
            articleId,
            organizationLegacyId: customerId,
        })

        const latestCyclesRevision = configuratorMaterial?.latestCyclesRevision
        if (!latestCyclesRevision) return null
        return firstValueFrom(this.sceneManagerCacheService.materialGraphCache.evaluateMaterialRevision(latestCyclesRevision.legacyId, flatten))
    }

    private readonly latestMaterialRevisionChanged$ = new Subject<number>()

    latestMaterialRevisionChanged(): Observable<number> {
        return this.latestMaterialRevisionChanged$
    }

    // the latest revision a material had when first loaded is kept while the template is open, so a revision uploaded
    // from Blender since has to take its place in every reference that follows the latest
    reloadLatestMaterialRevision(materialId: number, latestRevisionLegacyId: number) {
        const {materialGraphCache} = this.sceneManagerCacheService
        const cached = materialGraphCache.cachedLatestRevision(materialId)
        if (cached === undefined || cached === latestRevisionLegacyId) return
        materialGraphCache.forgetLatestRevision(materialId)
        this.latestMaterialRevisionChanged$.next(materialId)
    }

    loadMaterial(materialId: number, materialRevisionId: number | null, flatten: boolean) {
        return firstValueFrom(
            materialRevisionId === null
                ? this.sceneManagerCacheService.materialGraphCache.evaluateMaterial(materialId, flatten)
                : this.sceneManagerCacheService.materialGraphCache.evaluateMaterialRevision(materialRevisionId, flatten),
        )
    }

    evaluateMesh(graph: MeshNodes.Mesh, resources: DataNodes.EvaluationResources): Promise<ReifiedMeshData> {
        return firstValueFrom(this.sceneManagerCacheService.meshCache.evaluateMesh(graph, resources))
    }

    getCachedMesh(graph: MeshNodes.Mesh): ReifiedMeshData | null {
        return this.sceneManagerCacheService.meshCache.getIfCached(graph)
    }

    loadTemplateGraph(templateId: number, templateRevisionId: number | null) {
        return firstValueFrom(
            templateRevisionId === null
                ? this.sceneManagerCacheService.templateRevisionGraphCache.evaluateTemplate(templateId)
                : this.sceneManagerCacheService.templateRevisionGraphCache.evaluateTemplateRevision(templateId, templateRevisionId),
        )
    }

    clipAndOffsetMeshForDecal(mesh: ReifiedMeshData, uvCenter: [number, number], uvRotation: number, size: [number, number], offset: number) {
        return firstValueFrom(this.sceneManagerCacheService.decalMeshCache.clipAndOffsetMeshForDecal(mesh, uvCenter, uvRotation, size, offset))
    }

    getMeshGeometryQueries(): IMeshGeometryQueries {
        return this.meshGeometryService
    }

    sceneModifyingStatus() {
        if (this.$currentlyModifyingScene()) {
            return {inProgress: true, onFinished: firstValueFrom(this.finishedModifyingScene$)}
        } else {
            return {inProgress: false as const}
        }
    }

    evaluateGeometry(graph: MeshNodes.GeomGraph, resources: DataNodes.EvaluationResources) {
        return firstValueFrom(this.sceneManagerCacheService.geometryDataCache.evaluateGeometry(graph, resources))
    }

    getGeometryBuilderContext(): GeomBuilderContext {
        if (!this.geometryBuilderContext) this.geometryBuilderContext = new GeomBuilderContext()
        return this.geometryBuilderContext
    }

    bakeTexture(textureNode: HashedMaterialNodeWithColorOutput, widthUV: number, heightUV: number, widthPx: number, heightPx: number): Promise<TypedImageData> {
        return firstValueFrom(this.bakeTextureCache.get(textureNode, widthUV, heightUV, widthPx, heightPx))
    }

    getResolver(): DataResolver {
        return this.sceneManagerCacheService.dataResolverService.resolve
    }
}
